import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";
import type { AddressInput, ContactPayload, ContactPending, PersonInput } from "../contact/api";

/* ---------- types (wire contract: plan 2D "Endpoint summary (as built)") ---------- */

export type ReportStatus = "visited" | "postponed";
/** 1–5; 5 = "Not interested" (one alert to the managers, once per visit). */
export type ReportReaction = 1 | 2 | 3 | 4 | 5;
export type RemindTarget = "me" | "managers" | "both";

/** `on` Y-m-d, today or later. Changing the date re-arms the reminder. */
export type ReportRemind = { target: RemindTarget; on: string; note?: string | null; planVisit?: boolean };

/** The kinds a report's changes become (one request per kind). */
export type RequestKind = "details" | "new_people" | "people_left" | "address_problem" | "left_town" | "move";
export type RequestStatus = "pending" | "approved" | "rejected" | "read" | "withdrawn" | "answered_elsewhere";

/**
 * `changes.details`: a subset of 2C's edit form (no `version`). Persons are named by `id`; an address `id` is
 * refused (an address is always typed again); `primary` must name an address typed in the same report.
 */
export type ReportDetailsChange = Pick<ContactPayload, "house_phone" | "home_address" | "primary"> & {
  persons?: (Omit<PersonInput, "key"> & { id: number })[];
};

/** Who left: some people (removed on approval), the whole household ("Left town"), or a move to a new address. */
export type ReportLeft =
  | { who: "people"; person_ids: number[] }
  | {
      who: "household";
      new_address?: AddressInput | null;
      address_type?: "home" | "work";
      /** Required for a work address. */
      work_person_id?: number;
      set_primary?: boolean;
    };

export type AddressProblem = "not_found" | "not_at_address";

/** "Anything to update?" in the wire shape (keys snake_case, as the 2C form). `null` withdraws on an edit. */
export type ReportChanges = {
  details?: ReportDetailsChange | null;
  new_people?: Omit<PersonInput, "id" | "key">[] | null;
  left?: ReportLeft | null;
  address_problem?: { problem: AddressProblem } | null;
};

export type ReportInput = {
  status: ReportStatus;
  /** Y-m-d, 2000-01-01 … today. */
  date: string;
  reaction?: ReportReaction | null;
  details?: string | null;
  /** `null` clears it. */
  remind?: ReportRemind | null;
  /** Filing only: the route stop the report was opened from. */
  pointId?: number;
  /** Filing only: marks the stop when the server's stop rule allows (else `stop_marked: false`). */
  markStopVisited?: boolean;
  changes?: ReportChanges;
};
/** PATCH reports/{visit}: the stop keys are not taken. */
export type ReportEditInput = Omit<ReportInput, "pointId" | "markStopVisited">;

/** GET households/{id}/report-form. The people / address part comes from the contact (`useContact`). */
export type ReportForm = {
  pending: ContactPending;
  /** `already_visited_at` (UTC ISO) only when the stop is already visited. */
  route_stop?: { point_id: number; route_id: number; route_name: string; stop_number: number; stops_total: number; already_visited_at?: string };
  can: { apply_at_once: boolean; remind_managers: boolean };
  version: string;
};

export type ReportRequest = {
  id: number;
  kind: RequestKind;
  status: RequestStatus;
  /** UTC `Y-m-d\TH:i:s\Z`. */
  decided_at?: string;
  /** Only when one was written, and only to approvers, the filer and the requester. */
  reason?: string;
  reason_by?: { name: string };
};

/** The card as filing answers it (no history context). */
export type ReportCardBase = {
  id: number;
  /** Y-m-d; null for a row without a date. */
  date: string | null;
  status: ReportStatus;
  reaction?: ReportReaction;
  details: string | null;
  /** Absent when the filer is unknown. */
  filed_by?: { id: number; name: string; is_me: boolean };
  /** 5A: filed or edited by ConnectYid staff viewing as `filed_by`; `staff_name` only for staff readers. */
  by_staff?: { staff_name: string | null } | null;
  /** With contact details only: the address it was filed at (null when there was none). */
  address_line?: string | null;
};

/** One report of the visit history (and an edit's answer). */
export type ReportCard = ReportCardBase & {
  /** UTC `Y-m-d\TH:i:s\Z`: the report's last save. */
  filed_at: string | null;
  requests: ReportRequest[];
  /**
   * `note`: not sent today (an edit then starts the reminder without its "About" text, and saving clears it — the
   * edit states the whole report); read when the server adds it.
   */
  reminder?: { on: string; target: RemindTarget; is_for_me: boolean; note?: string | null };
  can: { edit: boolean; delete: boolean };
};

export type ScheduledVisit = { date: string; weekday_key: string; is_next: boolean; note_key?: "postponed_moved"; from_date?: string };

export type HistoryPage = {
  data: ReportCard[];
  meta: { page: number; last_page: number; total_past: number; total_scheduled: number };
  /** Page 1 only. */
  scheduled?: ScheduledVisit[];
};

export type FiledRequest = { id: number; kind: RequestKind; status: RequestStatus };
export type FileResult = {
  visit_id: number;
  report: ReportCardBase;
  requests: FiledRequest[];
  applied_at_once: RequestKind[];
  stop_marked: boolean;
};
export type EditResult = { report: ReportCard; requests: FiledRequest[] };

/* ---------- wire ---------- */

/** The body the server takes: snake_case keys, absent keys left out, `null` kept (it clears). */
export const toReportWire = (input: ReportInput): Record<string, unknown> => {
  const { pointId, markStopVisited, remind, ...rest } = input;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) if (v !== undefined) out[k] = v;
  if (remind !== undefined) {
    out.remind = remind === null ? null : {
      target: remind.target,
      on: remind.on,
      ...(remind.note !== undefined ? { note: remind.note } : {}),
      ...(remind.planVisit !== undefined ? { plan_visit: remind.planVisit } : {}),
    };
  }
  if (pointId !== undefined) out.point_id = pointId;
  if (markStopVisited !== undefined) out.mark_stop_visited = markStopVisited;
  return out;
};

/* ---------- keys and refreshes (shared with approvals) ---------- */

const BASE = "/api/auth/next";

export const visitsKey = (ws: number | null, householdId: number, ...parts: unknown[]) => scopedKey(ws, "visits", householdId, ...parts);

/**
 * After anything that changes a contact's reports or details: the contact (page, history of changes), its visits
 * (history, report form) and the list (last visit, pending marks).
 */
export const refreshContact = (client: QueryClient, ws: number | null, householdId: number) =>
  Promise.all([
    client.invalidateQueries({ queryKey: scopedKey(ws, "contacts") }),
    client.invalidateQueries({ queryKey: scopedKey(ws, "contact", householdId) }),
    client.invalidateQueries({ queryKey: visitsKey(ws, householdId) }),
  ]);

/** The bell: the "To approve" list and its reviews, the Updates feed and the counts. */
export const refreshInbox = (client: QueryClient, ws: number | null) =>
  Promise.all([
    client.invalidateQueries({ queryKey: scopedKey(ws, "approvals") }),
    client.invalidateQueries({ queryKey: scopedKey(ws, "inbox") }),
  ]);

/* ---------- queries ---------- */

export const useReportForm = (householdId: number, pointId?: number, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: visitsKey(ws, householdId, "form", pointId ?? null),
    queryFn: async () =>
      (await api.get<{ data: ReportForm }>(`${BASE}/households/${householdId}/report-form`, pointId ? { point_id: pointId } : {})).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** 10 past reports per page, newest first; `scheduled` on the first page. Needs read_history (else 403). */
export const useVisitHistory = (householdId: number, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: visitsKey(ws, householdId, "history"),
    queryFn: ({ pageParam }) => api.get<HistoryPage>(`${BASE}/households/${householdId}/reports`, { page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/* ---------- mutations ---------- */

/** POST a report's wire body (the form's, or a draft's as it was last sent: F5's re-send when the connection is back). */
export const postReport = async (householdId: number, wire: Record<string, unknown>, idempotencyKey?: string): Promise<FileResult> =>
  (
    await api.post<{ data: FileResult }>(
      `${BASE}/households/${householdId}/reports`,
      wire,
      idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : undefined,
    )
  ).data;

/**
 * File a report. `idempotencyKey` is the caller's (kept with the draft): "Try again" sends the same key, so a save
 * that reached the server is never filed twice. Requests it filed reach the bell, so that refreshes too.
 */
export const useFileReport = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { householdId: number; input: ReportInput; idempotencyKey?: string }) =>
      postReport(v.householdId, toReportWire(v.input), v.idempotencyKey),
    // In the background: the form closes as soon as the report is saved, not after every list has reloaded.
    onSuccess: (_r, v) => {
      void Promise.all([refreshContact(client, ws, v.householdId), refreshInbox(client, ws)]);
      // Filed from a route stop: the stop may now be ticked, so the routes (page, map, list counts) reload too.
      if (v.input.pointId !== undefined) void client.invalidateQueries({ queryKey: scopedKey(ws, "routes") });
    },
  });
};

export const useEditReport = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (v: { visitId: number; householdId: number; input: ReportEditInput }) =>
      (await api.patch<{ data: EditResult }>(`${BASE}/reports/${v.visitId}`, toReportWire(v.input))).data,
    // In the background: the form closes as soon as the report is saved, not after every list has reloaded.
    onSuccess: (_r, v) => { void Promise.all([refreshContact(client, ws, v.householdId), refreshInbox(client, ws)]); },
  });
};

/** Its pending requests are withdrawn with it, so the bell refreshes too. */
export const useDeleteReport = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (v: { visitId: number; householdId: number }) => {
      await api.del<void>(`${BASE}/reports/${v.visitId}`);
    },
    // The history and the contact (its "next planned") reload before the mutation ends, so the dialog keeps its busy
    // sign until the deleted report is gone from the list; the list of contacts and the bell follow in the background.
    onSuccess: async (_r, v) => {
      void client.invalidateQueries({ queryKey: scopedKey(ws, "contacts") });
      void refreshInbox(client, ws);
      await Promise.all([
        client.invalidateQueries({ queryKey: scopedKey(ws, "contact", v.householdId) }),
        client.invalidateQueries({ queryKey: visitsKey(ws, v.householdId) }),
      ]);
    },
  });
};

/** Approvers with update rights (`left_town.can_undo`): visits are planned again. */
export const useUndoLeftTown = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (householdId: number) =>
      (await api.post<{ data: { left_town: false } }>(`${BASE}/households/${householdId}/undo-left-town`)).data,
    onSuccess: (_r, householdId) => refreshContact(client, ws, householdId),
  });
};
